import React from 'react';
import PropTypes from 'prop-types';

class ModalVideo extends React.Component {
  static propTypes = {
    videoId: PropTypes.string.isRequired,
    onClose: PropTypes.func.isRequired,
    // eslint-disable-next-line react/no-unused-prop-types
    channel: PropTypes.oneOf(['vimeo', 'youtube']),
    // eslint-disable-next-line react/no-unused-prop-types
    youtube: PropTypes.shape({
      autoplay: PropTypes.number,
      cc_load_policy: PropTypes.number,
      color: PropTypes.string,
      controls: PropTypes.number,
      disablekb: PropTypes.oneOf([0, 1]),
      enablejsapi: PropTypes.oneOf([0, 1]),
      end: PropTypes.number,
      fs: PropTypes.oneOf([0, 1]),
      h1: PropTypes.string,
      iv_load_policy: PropTypes.oneOf([0, 1]),
      list: PropTypes.number,
      listType: PropTypes.string,
      loop: PropTypes.oneOf([0, 1]),
      modestbranding: PropTypes.string,
      origin: PropTypes.string,
      playlist: PropTypes.string,
      playsinline: PropTypes.bool,
      rel: PropTypes.number,
      showinfo: PropTypes.oneOf([0, 1]),
      start: PropTypes.number,
      wmode: PropTypes.string,
      theme: PropTypes.string,
    }),
    ratio: PropTypes.string,
    // eslint-disable-next-line react/no-unused-prop-types
    vimeo: PropTypes.shape({
      api: PropTypes.bool,
      autopause: PropTypes.bool,
      autoplay: PropTypes.bool,
      byline: PropTypes.bool,
      callback: PropTypes.func,
      color: PropTypes.string,
      height: PropTypes.number,
      loop: PropTypes.bool,
      maxheight: PropTypes.number,
      maxwidth: PropTypes.number,
      player_id: PropTypes.string,
      portrait: PropTypes.bool,
      title: PropTypes.bool,
      width: PropTypes.string,
      xhtml: PropTypes.bool,
    }),
    allowFullScreen: PropTypes.bool,
    classNames: PropTypes.shape({
      modalVideoEffect: PropTypes.string,
      modalVideo: PropTypes.string,
      modalVideoClose: PropTypes.string,
      modalVideoBody: PropTypes.string,
      modalVideoInner: PropTypes.string,
      modalVideoIframeWrap: PropTypes.string,
      modalVideoCloseBtn: PropTypes.string,
    }),
    aria: PropTypes.shape({
      openMessage: PropTypes.string,
      dismissBtnMessage: PropTypes.string,
    }),
  };

  static defaultProps = {
    channel: 'youtube',
    isOpen: false,
    youtube: {
      autoplay: 1,
      cc_load_policy: 1,
      color: null,
      controls: 1,
      disablekb: 0,
      enablejsapi: 0,
      end: null,
      fs: 1,
      h1: null,
      iv_load_policy: 1,
      list: null,
      listType: null,
      loop: 0,
      modestbranding: null,
      origin: null,
      playlist: null,
      playsinline: null,
      rel: 0,
      showinfo: 1,
      start: 0,
      wmode: 'transparent',
      theme: 'dark',
    },
    ratio: '16:9',
    vimeo: {
      api: false,
      autopause: true,
      autoplay: true,
      byline: true,
      callback: null,
      color: null,
      height: null,
      loop: false,
      maxheight: null,
      maxwidth: null,
      player_id: null,
      portrait: true,
      title: true,
      width: null,
      xhtml: false,
    },
    allowFullScreen: true,
    animationSpeed: 300,
    classNames: {
      modalVideoEffect: 'modal-video-effect',
      modalVideo: 'modal-video',
      modalVideoClose: 'modal-video-close',
      modalVideoBody: 'modal-video-body',
      modalVideoInner: 'modal-video-inner',
      modalVideoIframeWrap: 'modal-video-movie-wrap',
      modalVideoCloseBtn: 'modal-video-close-btn',
    },
    aria: {
      openMessage: 'You just opened the modal video',
      dismissBtnMessage: 'Close the modal by clicking here',
    },
  };

  state = {
    isOpen: false,
  };

  // eslint-disable-next-line
  UNSAFE_componentWillReceiveProps(nextProps) {
    this.setState({ isOpen: nextProps.isOpen });
  }

  componentDidUpdate() {
    if (this.state.isOpen) {
      this.modal.focus();
    }
  }

  // eslint-disable-next-line class-methods-use-this
  getQueryString(obj) {
    let url = '';

    Object.keys(obj).forEach(key => {
      if (obj[key] !== null) {
        url += `${key}=${obj[key]}&`;
      }
    });
    return url.substr(0, url.length - 1);
  }

  getYoutubeUrl(youtube, videoId) {
    const query = this.getQueryString(youtube);
    return `//www.youtube.com/embed/${videoId}?${query}`;
  }

  getVimeoUrl(vimeo, videoId) {
    const query = this.getQueryString(vimeo);
    return `//player.vimeo.com/video/${videoId}?${query}`;
  }

  getVideoUrl(opt, videoId) {
    if (opt.channel === 'youtube') {
      return this.getYoutubeUrl(opt.youtube, videoId);
    }
    if (opt.channel === 'vimeo') {
      return this.getVimeoUrl(opt.vimeo, videoId);
    }

    return undefined;
  }

  // eslint-disable-next-line class-methods-use-this
  getPadding(ratio) {
    const arr = ratio.split(':');
    const width = Number(arr[0]);
    const height = Number(arr[1]);
    const padding = (height * 100) / width;
    return `${padding}%`;
  }

  closeModal = () => {
    this.setState(() => ({ isOpen: false }));
    if (typeof this.props.onClose === 'function') {
      this.props.onClose();
    }
  };

  updateFocus = event => {
    if (event.keyCode === 9) {
      event.preventDefault();
      event.stopPropagation();
      if (this.modal === document.activeElement) {
        this.modalbtn.focus();
      } else {
        this.modal.focus();
      }
    }
  };

  render() {
    const style = {
      paddingBottom: this.getPadding(this.props.ratio),
    };

    return this.state.isOpen ? (
      // eslint-disable-next-line
      <div
        className={this.props.classNames.modalVideo}
        tabIndex="-1"
        role="dialog"
        aria-label={this.props.aria.openMessage}
        onClick={this.closeModal}
        ref={node => {
          this.modal = node;
        }}
        onKeyDown={this.updateFocus}
      >
        <div className={this.props.classNames.modalVideoBody}>
          <div className={this.props.classNames.modalVideoInner}>
            <div
              className={this.props.classNames.modalVideoIframeWrap}
              style={style}
            >
              <button
                className={this.props.classNames.modalVideoCloseBtn}
                aria-label={this.props.aria.dismissBtnMessage}
                ref={node => {
                  this.modalbtn = node;
                }}
                onKeyDown={this.updateFocus}
              />

              <iframe
                title={this.props.videoId}
                width="460"
                height="230"
                src={this.getVideoUrl(this.props, this.props.videoId)}
                frameBorder="0"
                allowFullScreen={this.props.allowFullScreen}
                tabIndex="-1"
              />
            </div>
          </div>
        </div>
      </div>
    ) : null;
  }
}

export default ModalVideo;
